import type { Profile } from '@up2stay/up2promo-api-client';
import { stripTrailingSlash } from '@up2stay/up2promo-commons/auth';

import { env } from '@/core/env';

type Locale = string;

/**
 * Constructs a profile URL based on the profile type and user information.
 *
 * @param params - Parameters for URL construction
 * @param params.profile - The profile object (must have workspace if it's a workspace profile)
 * @param params.username - The username of the profile owner
 * @param params.locale - The locale/language code (e.g., 'de', 'en')
 * @param params.workspaceUsername - Workspace username (slug). Required for building the profile URL.
 * @param params.baseUrl - Unused; the base is always VITE_PROFILE_VIEWER_DOMAIN
 * @returns The constructed profile URL, or null if required parameters are missing
 *
 * @example
 * ```ts
 * // Workspace profile with slug
 * const url = buildProfileUrl({
 *   profile: { workspace: 'workspace123' },
 *   username: 'johndoe',
 *   locale: 'de',
 *   workspaceUsername: 'company-name',
 * });
 * // Returns: 'https://profile.up2promo.com/company-name/johndoe'
 *
 * // Workspace profile (workspaceUsername used as path segment)
 * const url = buildProfileUrl({
 *   profile: { workspace: 'workspace123' },
 *   username: 'johndoe',
 *   locale: 'de',
 *   workspaceUsername: 'workspace123',
 * });
 * // Returns: 'https://profile.up2promo.com/workspace123/johndoe'
 *
 * ```
 */
export function buildProfileUrl({
  profile,
  username,
  workspaceUsername,
}: {
  profile: Pick<Profile, 'workspace'> | null;
  username: string | null | undefined;
  locale: Locale;
  workspaceUsername?: string | null;
  baseUrl?: string;
}): string | null {
  if (!profile || !username) {
    return null;
  }
  // Profiles are workspace-scoped only; no personal profile URL
  if (!profile.workspace || !workspaceUsername) {
    return null;
  }
  const resolvedBaseUrl = stripTrailingSlash(env.VITE_PROFILE_VIEWER_DOMAIN);
  return `${resolvedBaseUrl}/${workspaceUsername}/${username}`;
}
